import { Save, Cancel } from "@mui/icons-material";
import { Box, Typography, Grid, IconButton, Tooltip, Divider } from "@mui/material";
import useDialogModal from "../../hooks/useDialogModal";
import { ButtonWithIcon, TabPanel, a11yProps } from "../ui";
import AddNewCustomerDialog from "./dialog/addNewCustomerDialog";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useCan } from "../../hooks/useCan";
import { useLocation, useParams } from 'react-router-dom';
import SearchCustomerDialog from "./dialog/searchCustomer/searchCustomerDialog";
import ProfileTab from "./tabs/profileTab";
import { Colors } from "../../styles/theme";
import { CustomerService } from "../../service/customerService";
import validLookup from "../../utils/validLookup";
import ConfirmationDialog from "../ui/confirmationDialog";
import { toast } from "react-toastify";
import { isLocalDev } from "../../utils/environment";
import FamilyMembers from "./familyMembers/familyMembers";
import AppointmentTab from "./tabs/appointmentTab";
import RxTab from "./tabs/rx/rxTab";
import InvoiceTab from "./tabs/invoice/invoice";
import SessionStorageUtils from "../../utils/sessionStorageUtils";
import Insurance from "./tabs/insurancePolicy/insurancePolicy";
import CustomerSearch from "../customerSearch/customerSearch";
import EyeExamTab from "./tabs/eyeExam/EyeExamTab";
import CreditMemo from "./tabs/creditMemo/creditMemo";
import CommunicationTab from "./tabs/communicationTab";
import { getAccessibleTabs, getTabIndexFromQueryParam } from "./customerRecordTabs";
import Can from "../auth/Can";
import CustomerInfobar from "./customerInfobar";
import CustomerDocuments from "./tabs/documents/customerDocuments";


export default function CustomerRecord() {
    const can = useCan();
    const location = useLocation();
    const { customerId } = useParams();
    const [dialogMessage, setDialogMessage] = useState();
    const [tabIndex, settTabIndex] = useState(0);
    const [profileDirty, setProfileDirty] = useState(false);
    
    // Get accessible tabs based on user permissions
    const accessibleTabs = useMemo(() => getAccessibleTabs(can), [can]);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);
    // Only need the dialog components here; open handlers are invoked where needed
    const [AddCustomerDialog] = useDialogModal(AddNewCustomerDialog);
    const [SearchForCustomerDialog] = useDialogModal(SearchCustomerDialog);

    const profileTabRef = useRef(null);

    const [customerData, setCustomerData] = useState({ customer: null, familyMembers: [] });

    /**
     * we need a way to know when a form is touched so that
     * actions can be taken  e.g. show the save/cancel button
     * and prevent tab change
     */
    const formTouched = useMemo(() => ({
        onTouched: (dirty) => {
            setProfileDirty(dirty);
        },
    }), []);

    /**
     * Load customer data
     */
    const loadCustomer = useCallback((id) => {
        try {
            CustomerService.getById(id).then((response) => {
                const { code, data, message } = { ...response };
                if (code === 0) {
                    resetCustomerData();
                    setCustomerData({ customer: data.customer, familyMembers: data.familyMembers });
                    SessionStorageUtils.set('lastCustomerId', data.customer.id, 30);
                }
                else toast.error(message);
            });
        } catch (error) {
            if (isLocalDev) console.log(error);
        }
    }, []);

    useEffect(() => {
        // Avoid double-loading when a route param is present; let the route-driven effect handle it
        if (customerId) return;
        const lastCustomerId = SessionStorageUtils.get('lastCustomerId');
        if (lastCustomerId) {
            loadCustomer(lastCustomerId);
        }
    }, [loadCustomer, customerId]);

    // If route param provided, prefer it (and sync into appState so rest of app stays consistent)
    useEffect(() => {
        if (customerId && Number(customerId) > 0) {
            // Only reload if different from current selected
            if (true) {
                loadCustomer(customerId);
            }
        }
    }, [customerId, loadCustomer]);

    // Read ?tab= from query string to preselect a tab (e.g., tab=chart)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam) {
            const tabIdx = getTabIndexFromQueryParam(tabParam, can);
            if (tabIdx !== null) {
                settTabIndex(tabIdx);
            }
        }
    }, [location.search, can]);

    // Validate current tab is accessible (redirect to first accessible tab if not)
    useEffect(() => {
        // tabIndex should be a position in the accessibleTabs array (0, 1, 2...)
        // not an original tab index (0, 2, 3, 5...)
        if (accessibleTabs.length > 0 && tabIndex >= accessibleTabs.length) {
            settTabIndex(0);
        }
    }, [tabIndex, accessibleTabs]);

    /**
     * when a tab is changed
     */
    const handleTabChange = (event, newValue) => {
        // const activeTab = getActiveTab(newValue);

        // switch(activeTab) {
        //     case TAB_ENUM.PROFILE:
        //         console.log('load profile');
        //     break;
        //     case TAB_ENUM.COMMENTS:
        //         CommentService.getCommentsWithLimits(1, {offset: 0, limit: pageSize}).then(response => {
        //             console.log(response);
        //         });
        //     break;
        //     default:
        //         throw new Error();
        // }

        if (profileDirty && tabIndex !== newValue) {
            setDialogMessage("You have unsaved changes, continue?");
            showConfirmationDialog((action) => {
                if (action === validLookup.confirmationDialogAction.ok) {
                    setProfileDirty(false);
                    settTabIndex(newValue);
                    closeConfirmationDialog();
                }
            });
        } else {
            settTabIndex(newValue);
        }
    };

    /**
     *
     * is called after customer search is complete and user has selected
     * a customer row
     */
    const handleSearchDone = (customer) => {

        if (customer) {
            //resetCustomerData();
            loadCustomer(customer.id);
            //setSelectedCustomer(customer);
        }
    };

    /**
     * when a family member is selected   
     */
    const handleFamilyMemberClicked = (familyMember) => {
        loadCustomer(familyMember.id);
    }

    const resetCustomerData = () => {
        setCustomerData({ customer: null, familyMembers: [] });

        // setCustomerData({
        //     profile: null,
        //     invoice: null,
        //     appointment: null,
        //     chart: null,
        //     rx: null,
        //     familyMembers: []
        // });
    }

    const onSaveHanlder = () => {
        profileTabRef.current?.save();
    };

    const onCancelHanlder = () => {
        profileTabRef.current?.cancel();
        setProfileDirty(false);
    };

    const handleAccountDelete = () => {
        setDialogMessage("Delete this customer? This will deactivate the account, detach them from any family group, and keep invoices, RX, insurance, and credit memos for history.");
        showConfirmationDialog(async (action) => {
            if (action !== validLookup.confirmationDialogAction.ok) return;

            try {
                const response = await CustomerService.remove(customerData?.customer?.id);
                const { code, message } = { ...response };

                if (code === 0) {
                    toast.success(message || "Customer deleted");
                    resetCustomerData();
                    SessionStorageUtils.remove('lastCustomerId');
                    settTabIndex(0);
                } else {
                    toast.warn(message || "Unable to delete customer");
                }
            } catch (error) {
                if (isLocalDev) console.log(error);
                toast.error("Something went wrong while deleting");
            } finally {
                closeConfirmationDialog();
            }
        });
    }

    const handleCustomerSelect = (customer) => {
        if (customer)
            loadCustomer(customer.id);
    };

    return (
        <>
            <Box display={"flex"} justifyContent="flex-end">
                <CustomerSearch onCustomerSelected={handleCustomerSelect} reset={true} sx={{ width: '20%' }} />
                <Can section="customers" action='edit'>
                    {profileDirty && (
                        <>
                            <ButtonWithIcon
                                sx={{ mr: 1, background: Colors.success }}
                                icon={<Save />}
                                title="Save"
                                onClickHandler={() => onSaveHanlder()}
                            />
                            <ButtonWithIcon
                                sx={{ mr: 1, background: Colors.muted }}
                                icon={<Cancel />}
                                title="Cancel"
                                onClickHandler={() => onCancelHanlder()}
                            />
                        </>
                    )}
                </Can>

                {/* <ButtonWithIcon
                    sx={{ mr: 1 }}
                    icon={<Add />}
                    title="Add new"
                    onClickHandler={() => showAddCustomerDialog()}
                />
                <ButtonWithIcon
                    icon={<Search />}
                    title="Search"
                    onClickHandler={() => showSearchCustomerDialog()}
                /> */}
            </Box>
            {customerData.customer && (
                <>
                    <CustomerInfobar customerData={customerData} loadCustomer={loadCustomer} />
                    <Box>
                        <Can section="customers" action='link-family'>
                            <Divider sx={{ mb: 1 }} />
                            <Typography sx={{ fontSize: '1.25rem', mb: 1 }}>FamilyMembers</Typography>
                            <FamilyMembers
                                linkTo={customerData.customer}
                                familyMemberClicked={handleFamilyMemberClicked}
                                familyMembers={customerData.familyMembers}
                            />
                        </Can>

                    </Box>

                </>
            )}

            {customerData.customer ? (
                <>
                    {customerData.familyMembers ?
                        <Grid container columnSpacing={0.25} sx={{ mt: 1 }}>
                            {/* <Grid item xs={12} sm={2}>
                                <FamilyMembers
                                    linkTo={customerData.customer}
                                    familyMemberClicked={handleFamilyMemberClicked}
                                    familyMembers={customerData.familyMembers}
                                />
                            </Grid> */}
                            <Grid item xs={12}>
                                <Box sx={{ width: "100%" }} flexGrow={1}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <Tabs value={tabIndex} onChange={handleTabChange}>
                                            {accessibleTabs.map((tab, idx) => (
                                                <Tab 
                                                    key={tab.key}
                                                    label={tab.label} 
                                                    {...a11yProps("customerrecord", idx)} 
                                                />
                                            ))}
                                        </Tabs>
                                    </Box>
                                    {/* Profile Tab - index 0 */}
                                    {accessibleTabs[tabIndex]?.index === 0 && (
                                        <TabPanel
                                            idfor="customerrecord"
                                            value={tabIndex}
                                            index={tabIndex}
                                            background={Colors.dove_gray}
                                        >
                                            {customerData.customer && (
                                                <ProfileTab
                                                    ref={profileTabRef}
                                                    deleteListener={handleAccountDelete}
                                                    formChanged={formTouched.onTouched}
                                                    profileData={customerData.customer}
                                                    onUpdate={(updatedData) =>
                                                        setCustomerData({ ...customerData, customer: updatedData })
                                                    }
                                                />
                                            )}
                                        </TabPanel>
                                    )}
                                    {/* Rx Tab - index 1 */}
                                    {accessibleTabs[tabIndex]?.index === 1 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <RxTab customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Invoice Tab - index 2 */}
                                    {accessibleTabs[tabIndex]?.index === 2 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <InvoiceTab customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Insurance Tab - index 3 */}
                                    {accessibleTabs[tabIndex]?.index === 3 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <Insurance customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Credit Memos Tab - index 4 */}
                                    {accessibleTabs[tabIndex]?.index === 4 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <CreditMemo customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Chart/EyeExam Tab - index 5 */}
                                    {accessibleTabs[tabIndex]?.index === 5 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <EyeExamTab customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Appointments Tab - index 6 */}
                                    {accessibleTabs[tabIndex]?.index === 6 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <AppointmentTab customer={customerData} />
                                        </TabPanel>
                                    )}
                                    {/* Documents Tab - index 7 */}
                                    {accessibleTabs[tabIndex]?.index === 7 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <CustomerDocuments customer={customerData}/>
                                        </TabPanel>
                                    )}
                                    {/*don't need for now Timeline Tab - index 8 */}
                                    {/* {accessibleTabs[tabIndex]?.index === 8 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <CustomerTimeline />
                                        </TabPanel>
                                    )} */}
                                    {/* Communications Tab - index 9 */}
                                    {accessibleTabs[tabIndex]?.index === 8 && (
                                        <TabPanel idfor="customerrecord" value={tabIndex} index={tabIndex}>
                                            <CommunicationTab customer={customerData} />
                                        </TabPanel>
                                    )}
                                </Box>
                            </Grid>
                        </Grid> : <Typography variant="h5" color={Colors.muted}>Loading customer data...</Typography>
                    }
                </>
            ) : (
                <Typography
                    variant="h4"
                    textAlign={"center"}
                    sx={{ mt: "10%", color: Colors.muted }}
                >
                    Add or search for a customer
                </Typography>
            )}
            <AddCustomerDialog onDone={handleSearchDone} />
            <SearchForCustomerDialog onDone={handleSearchDone} />
            <ConfirmationDialogComp
                message={dialogMessage}
                label={{
                    positive: "Yes",
                    negative: "No",
                }}
            />
        </>
    );
}
